'use client';

import React, { useEffect, useRef, useState } from 'react';
import { FileIcon, MagicWandIcon, SpinnerGapIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Input } from '@tranquility-ai/theme-components/input';
import { Modal } from '@tranquility-ai/theme-components/modal';
import '@tranquility-ai/theme-components/modal.css';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { TIMELINE_TITLE_MAX_LENGTH } from '@/shared/constants';
import type { ScopesObject } from '@/shared/types';
import { emptyScopesNew } from '../chat-evidence-locker-context-new';
import { TimelineChoiceCards } from './timeline-choice-cards';
import './timeline-choice-cards.css';
import { TimelineScopePickerView } from './timeline-scope-picker-view';
import { TimelineScopeSummary } from './timeline-scope-summary';

export type TimelineStartFrom = 'blank' | 'autogen';

export type TimelineCreateModalProps = {
  isOpen: boolean;
  defaultName: string;
  caseId: string;
  /** When false the modal is name-only, matching Create New with the flag off. */
  autoGenerateEnabled: boolean;
  /** A timeline build is already running for this case. */
  isGenerating: boolean;
  onClose: () => void;
  onCreateBlank: (name: string) => void;
  onGenerate: (scopes: ScopesObject, name: string) => void;
};

export function TimelineCreateModal({
  isOpen,
  defaultName,
  caseId,
  autoGenerateEnabled,
  isGenerating,
  onClose,
  onCreateBlank,
  onGenerate,
}: TimelineCreateModalProps) {
  const t = useTimePilotT();
  const [view, setView] = useState<'details' | 'scope'>('details');
  const [name, setName] = useState(defaultName);
  const [mode, setMode] = useState<TimelineStartFrom>('blank');
  const [scopes, setScopes] = useState<ScopesObject>(() => emptyScopesNew());
  const [previewOpen, setPreviewOpen] = useState(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setView('details');
      setName(defaultName);
      setMode('blank');
      setScopes(emptyScopesNew());
      setPreviewOpen(false);
    }
    wasOpen.current = isOpen;
  }, [isOpen, defaultName]);

  const trimmedName = name.trim();
  const nameLength = trimmedName.length;
  const nameTooLong = nameLength > TIMELINE_TITLE_MAX_LENGTH;
  const nameValid = !!trimmedName && !nameTooLong;
  const autogen = autoGenerateEnabled && mode === 'autogen';
  const canSubmit = nameValid && !(autogen && isGenerating);

  const submit = () => {
    if (!canSubmit) return;
    if (autogen) {
      onGenerate(scopes, trimmedName);
      return;
    }
    onCreateBlank(trimmedName);
  };

  const footer = (
    <div className="flex w-full flex-row items-center justify-between gap-2">
      {view === 'scope' ? (
        <Button
          type="button"
          size="sm"
          style="text"
          color="light"
          background="inverse"
          onClick={() => {
            setPreviewOpen(false);
            setView('details');
          }}
        >
          {t('timelineCreate.back')}
        </Button>
      ) : (
        <Button
          type="button"
          size="sm"
          style="text"
          color="light"
          background="inverse"
          onClick={onClose}
        >
          {t('timeline.dialog.cancel')}
        </Button>
      )}
      <div className="flex flex-row items-center gap-2">
        {view === 'scope' ? (
          <Button
            type="button"
            size="sm"
            style="solid"
            color="subdued"
            background="inverse"
            onClick={() => {
              setPreviewOpen(false);
              setView('details');
            }}
          >
            {t('timelineCreate.done')}
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          style="solid"
          color="accent"
          background="inverse"
          disabled={!canSubmit}
          isLoading={autogen && isGenerating}
          loadingText={t('timelineCreate.generating')}
          onClick={submit}
        >
          {autogen ? t('timelineCreate.generateTimeline') : t('timelineCreate.createTimeline')}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={view === 'scope' ? t('timelineCreate.scopeEvidence') : t('timeline.select.createNew')}
      size={view === 'scope' ? 'xl' : 'md'}
      bodyClassName="timeline-modal-body-pad"
      dismissOnEscape={!previewOpen}
      dismissOnBackdropClick={!previewOpen}
      footer={footer}
    >
      {view === 'scope' ? (
        <TimelineScopePickerView
          caseId={caseId}
          scopes={scopes}
          onScopesChange={setScopes}
          onBack={() => {
            setPreviewOpen(false);
            setView('details');
          }}
          onPreviewPopoverOpenChange={setPreviewOpen}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {autoGenerateEnabled ? (
            <div className="flex flex-col gap-2">
              <p className="m-0 text-sm font-medium text-white">{t('timelineCreate.startFrom')}</p>
              <TimelineChoiceCards
                ariaLabel={t('timelineCreate.startFrom')}
                value={mode}
                onChange={(value) => setMode(value as TimelineStartFrom)}
                options={[
                  {
                    value: 'blank',
                    label: t('timelineCreate.blank'),
                    description: t('timelineCreate.blankDescription'),
                    icon: <FileIcon weight="duotone" />,
                  },
                  {
                    value: 'autogen',
                    label: t('timelineCreate.autoGenerate'),
                    description: isGenerating ? t('timelineCreate.generating') : t('timelineCreate.populateFromFiles'),
                    icon: isGenerating
                      ? <SpinnerGapIcon className="animate-spin-only" />
                      : <MagicWandIcon weight="duotone" />,
                    disabled: isGenerating,
                    loading: isGenerating,
                  },
                ]}
              />
              {isGenerating ? (
                <p className="m-0 text-xs text-white/60">
                  {t('timelineCreate.alreadyGenerating')}
                </p>
              ) : null}
            </div>
          ) : null}
          <Input
            label={t('timelineCreate.nameLabel')}
            placeholder={t('timelineCreate.namePlaceholder')}
            value={name}
            background="inverse"
            className="inverse-form-surface w-full"
            invalid={nameTooLong}
            error={
              nameTooLong
                ? t('timelineCreate.nameTooLong', { max: TIMELINE_TITLE_MAX_LENGTH })
                : undefined
            }
            description={`${nameLength}/${TIMELINE_TITLE_MAX_LENGTH}`}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && canSubmit && view === 'details') {
                submit();
              }
            }}
          />
          {autogen ? (
            <TimelineScopeSummary
              scopes={scopes}
              onChange={setScopes}
              inverse
              onAddEvidence={() => setView('scope')}
              onEdit={() => setView('scope')}
            />
          ) : null}
        </div>
      )}
    </Modal>
  );
}
